import React, { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { Skeleton } from "./ui";
import MonthSelect, { currentMonth, monthLabel } from "./MonthSelect";

/**
 * "My cricket" block for the dark hero box on the home screen: the logged-in
 * player's numbers, all time and for a chosen month. Renders nothing for
 * accounts that aren't a player in the club.
 */
export default function MyStatsCard({ refreshKey = 0 }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/stats/me", { params: { month } })
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month, refreshKey]);

  if (data && !data.linked) return null;

  const all = data?.all_time;
  const m = data?.this_month;

  return (
    <div className="px-5" data-testid="my-stats">
      <div className="rounded-3xl bg-ink text-white p-5 shadow-card">
        <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">My cricket • All time</div>
        {!all ? (
          <Skeleton className="h-12 mt-2 bg-white/10" />
        ) : (
          <div className="grid grid-cols-4 gap-2 mt-2" data-testid="my-stats-all-time">
            <Stat value={all.matches} label="Matches" big />
            <Stat value={all.runs} label="Runs" big />
            <Stat value={all.wickets} label="Wickets" big />
            <Stat value={all.days_attended} label="Days Played" big />
          </div>
        )}
        {all && all.matches > 0 && (
          <div className="text-xs text-white/50 mt-2">
            Best {all.highest}{all.highest_not_out ? "*" : ""}
            {all.best_bowling ? ` • Best bowling ${all.best_bowling}` : ""}
            {` • ${all.wins} win${all.wins === 1 ? "" : "s"}`}
          </div>
        )}

        <div className="mt-4 rounded-2xl bg-white/10 p-3">
          <div className="flex justify-center">
            <MonthSelect value={month} onChange={setMonth} activeMonths={data?.months || []} dark testid="my-stats-month" />
          </div>
          {loading || !m ? (
            <Skeleton className="h-12 mt-2 bg-white/10" />
          ) : m.matches === 0 && m.days_attended === 0 ? (
            <div className="text-sm text-white/60 mt-2">
              No cricket for you in {monthLabel(month)}
              {m.match_days > 0 && ` — the club had ${m.match_days} match day${m.match_days === 1 ? "" : "s"}`}.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="my-stats-month-tiles">
                <Stat value={m.matches} label="Matches" />
                <Stat value={m.runs} label="Runs" />
                <Stat value={m.wickets} label="Wickets" />
                <Stat value={`${m.days_attended}/${m.match_days}`} label="Days Played" />
              </div>
              <div className="text-xs text-white/50 mt-2">
                SR {m.balls ? m.strike_rate.toFixed(0) : "–"} • 4s/6s {m.fours}/{m.sixes} • Catches {m.catches + m.run_outs}
              </div>
            </>
          )}
          {!loading && data?.money && (
            <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/10" data-testid="my-stats-money">
              <div>
                {/* Everything this player still owes the club, whatever month it was charged in */}
                <div className={`text-lg font-extrabold tabular-nums ${data.money.pending > 0 ? "text-red-400" : "text-white"}`}>{inr(data.money.pending)}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">My pending</div>
              </div>
              <div>
                <div className="text-lg font-extrabold tabular-nums text-brand-100">{inr(data.money.paid)}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Paid this month</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ value, label, big }) {
  return (
    <div>
      <div className={`${big ? "text-3xl" : "text-xl"} font-extrabold tabular-nums leading-tight text-white`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider leading-tight text-white/50 font-semibold">{label}</div>
    </div>
  );
}
